import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ExternalLink, Hammer, KeyRound, LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWorkspaceStore } from '@/store/workspace.store';
import { WorkspaceSelector } from '@/components/common/WorkspaceSelector';
import { BotonVerComo, FranjaVerComo } from '@/components/common/VerComo';
import { viendoComo, volverAMiCuenta } from '@/lib/ver-como';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { miConversia, type MiConversiaDatos } from '@/api/negocios-cliente.api';
import { negocioPropio, vaAMiConversia, vistaDelPrograma } from '@/lib/mi-conversia';

/**
 * MI CONVERSIA — el programa real de un cliente reflejo o VIP.
 *
 * Ese programa es un sistema en producción que desde acá NO se toca: solo se
 * muestra por su URL pública. Si se deja meter en un marco, va a todo el alto;
 * si no, un botón lo abre en otra pestaña. Un pendiente ve que está en
 * preparación, y un negocio nuevo no tiene nada que hacer acá: va al panel.
 *
 * Va fuera del Layout con menú (como /mis-trabajos): el menú de Conversia no
 * le sirve de nada a quien vive en otro sistema.
 */
export default function MiConversia() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Esta página no pasa por el Layout: la lista de negocios la carga ella. Sin
  // negocio activo no se pide nada (el servidor no sabría de quién hablamos).
  const { cargado } = useWorkspaces();
  const items = useWorkspaceStore((s) => s.items);
  const activeId = useWorkspaceStore((s) => s.activeId);
  const activo = items.find((w) => w.id === activeId) ?? null;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['mi-conversia', activeId],
    queryFn: miConversia,
    enabled: cargado && vaAMiConversia(activo),
  });

  if (!cargado) return <Cargando />;
  // Misma decisión y mismo dato que el panel (AdminShell): no hay ida y vuelta.
  if (!vaAMiConversia(activo)) return <Navigate to="/" replace />;

  const propio = user?.role === 'OWNER' ? negocioPropio(items) : null;
  const volver = propio
    ? () => {
        useWorkspaceStore.getState().setActive(propio.id);
        // Lo cargado era del cliente: se tira antes de entrar al negocio propio.
        qc.clear();
        navigate('/negocios');
      }
    : null;

  return (
    <div className="flex h-dvh flex-col bg-surface-muted dark:bg-slate-950">
      <FranjaVerComo />
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-surface-border bg-white px-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex min-w-0 items-center gap-3">
          <img src="/conversia-logo.svg" alt="Conversia" className="h-7 w-7 shrink-0" />
          <span className="hidden text-sm font-bold text-brand-600 sm:inline">Conversia</span>
          <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {data?.nombre ?? activo?.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {volver && (
            <Button size="sm" variant="outline" onClick={volver}>
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Volver a todos los clientes</span>
            </Button>
          )}
          <WorkspaceSelector />
          <BotonVerComo />
          <span className="hidden max-w-[160px] truncate text-xs text-slate-500 md:inline dark:text-slate-400">
            {user?.name ?? user?.email}
          </span>
          <Button
            size="sm"
            variant="ghost"
            // Mirando otra cuenta, «Salir» vuelve a la de la administración.
            onClick={() => (viendoComo() ? volverAMiCuenta() : void logout())}
            aria-label="Salir"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Salir</span>
          </Button>
        </div>
      </header>

      <main className="min-h-0 flex-1">
        {isLoading ? (
          <Cargando />
        ) : isError || !data ? (
          <div className="p-4">
            <EmptyState
              icon={AlertTriangle}
              title="No se pudo abrir tu Conversia"
              description={(error as Error | null)?.message}
              action={
                <Button variant="outline" onClick={() => refetch()}>
                  Reintentar
                </Button>
              }
            />
          </div>
        ) : (
          // `key`: al cambiar de cliente con el selector, la nota y el marco arrancan de cero.
          <ProgramaDelCliente key={activeId} datos={data} />
        )}
      </main>
    </div>
  );
}

function Cargando() {
  return (
    <div className="flex h-full justify-center py-16">
      <Spinner size={28} className="text-brand-600" />
    </div>
  );
}

const CLAVE_NOTA = 'conversia.miConversia.notaVista:';

function notaYaVista(url: string): boolean {
  try {
    return localStorage.getItem(CLAVE_NOTA + url) === '1';
  } catch {
    return false;
  }
}

/**
 * Lo que va debajo de la barra. Exportado para probarlo sin red ni router.
 *
 * El marco NO lleva `sandbox`: el programa es de otro origen, así que de por sí
 * no puede leer ni tocar este panel; un `sandbox` solo agregaría formas de
 * romperle el login (almacenamiento, descargas, diálogos). `no-referrer` evita
 * que el programa reciba la dirección del panel.
 */
export function ProgramaDelCliente({ datos }: { datos: MiConversiaDatos }) {
  const vista = vistaDelPrograma(datos);
  const url = datos.programaUrl ?? '';
  const [nota, setNota] = useState(() => vista === 'marco' && !notaYaVista(url));

  if (vista === 'preparacion') {
    return (
      <div className="p-4">
        <EmptyState
          icon={Hammer}
          title="Tu Conversia está en preparación"
          description="Todavía no está en internet. Cuando esté lista, aparece aquí mismo."
          className="mx-auto mt-10 max-w-lg bg-white dark:bg-slate-900"
        />
      </div>
    );
  }

  if (vista === 'pestana') {
    return (
      <div className="p-4">
        <EmptyState
          icon={ExternalLink}
          title={datos.nombre}
          description="Tu programa se abre en una pestaña aparte. Entra con la clave de tu programa; tu navegador la recordará."
          className="mx-auto mt-10 max-w-lg bg-white dark:bg-slate-900"
          action={
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Abrir mi Conversia <ExternalLink className="h-4 w-4" />
            </a>
          }
        />
      </div>
    );
  }

  const cerrarNota = () => {
    setNota(false);
    try {
      localStorage.setItem(CLAVE_NOTA + url, '1');
    } catch {
      // Sin almacenamiento la nota vuelve a salir; no rompe nada.
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-surface-border bg-white px-4 py-1.5 text-xs dark:border-slate-800 dark:bg-slate-900">
        {nota ? (
          <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <KeyRound className="h-3.5 w-3.5 shrink-0 text-brand-600" />
            Entra con la clave de tu programa; tu navegador la recordará.
            <button type="button" onClick={cerrarNota} className="ml-1 font-medium text-brand-700 hover:underline dark:text-brand-300">
              Entendido
            </button>
          </span>
        ) : (
          <span />
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline dark:text-brand-300"
        >
          Abrir en pestaña aparte <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="min-h-0 flex-1">
        <iframe src={url} title={datos.nombre} referrerPolicy="no-referrer" className="h-full w-full border-0" />
      </div>
    </div>
  );
}
